import AddWidgetComponent from 'components/AddWidgetComponent';
import Widget from 'components/Widget';
import 'pages/Dashboard/assets/Dashboard.css';
import { useContext } from 'react';
import { Responsive, WidthProvider } from 'react-grid-layout';

import { LayoutSizeContext } from 'App';

const ResponsiveGridLayout = WidthProvider(Responsive);

export default function DashboardPreview({
  definedWidgetData,
  list,
  setList,
  onRemoveWidget,
  openWidgetSelector,
  uninteractable,
  edit = true,
  compactPreview = false,
}) {
  const layoutSize = useContext(LayoutSizeContext);
  const gridCols = compactPreview
    ? { xs: 1, sm: 1, md: 3, lg: 4, xl: 4, xxl: 4 }
    : { xs: 1, sm: 1, md: 1, lg: 2, xl: 4, xxl: 4 };
  const gridMargin = compactPreview ? [4, 4] : [10, 10];
  const gridContainerPadding = compactPreview ? [0, 0] : undefined;

  const previewRowHeight = compactPreview
    ? Math.max(95, Math.round((layoutSize.width || layoutSize.widgetWidth || 1000) / 4))
    : Math.round((layoutSize.width || layoutSize.widgetWidth || 1000) / 4);
  const previewAreaStyle = compactPreview
    ? {
        height: '100%',
        minHeight: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        boxSizing: 'border-box',
        paddingBottom: edit && !uninteractable ? 64 : 8,
      }
    : {
        maxHeight: '65vh',
        overflowX: 'hidden',
        overflowY: 'auto',
        boxSizing: 'border-box',
      };

  const onListChange = (newList) => {
    setList(newList);
  };

  const appendAddWidget = (arr) => [
    ...arr,
    {
      i: 'add',
      displayed: true,
      isDraggable: false,
      locked: true,
      static: false,
      x: 0,
      y: Infinity,
      w: 1,
      h: 1,
    },
  ];

  const createLayout = (list, uninteractable) => ({
    xxl: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: item.w,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    xl: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: item.w,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    lg: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: item.w,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    md: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: 1,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    sm: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: 1,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
    xs: appendAddWidget(
      list.map((item, index) => ({
        i: String(index),
        x: item.x ?? 0,
        y: item.y ?? 0,
        w: 1,
        h: item.h,
        isDraggable: uninteractable ? false : (item.isDraggable ?? true),
        static: item.static ?? false,
      })),
    ),
  });

  const layouts = createLayout(list ?? [], uninteractable);

  // Render widget preview (image or fallback) while preserving visuals.
  // Keeps grid rowHeight/layout unchanged and simplifies inline logic.
  const createPreviewComponent = (widgetDef, previewSrc) => (w, h, preview) => {
    if (!previewSrc) {
      return typeof widgetDef.component === 'function' ? widgetDef.component(w, h, preview) : null;
    }

    return (
      <img
        src={previewSrc}
        alt={`${widgetDef.displayName || 'Widget'} preview`}
        style={{
          width: '100%',
          height: '100%',
          objectFit: 'fill',
          objectPosition: 'center center',
        }}
      />
    );
  };

  return (
    <div className="w-100 h-100 d-flex flex-grow-full flex-fill">
      <ResponsiveGridLayout
        breakpoints={{ xs: 0, sm: 576, md: 768, lg: 992, xl: 1200, xxl: 1400 }}
        cols={gridCols}
        rowHeight={previewRowHeight}
        margin={gridMargin}
        containerPadding={gridContainerPadding}
        compactType="vertical"
        layouts={layouts}
        onDragStop={(layout) => {
          const temp = [...list];
          for (let i = 0; i < layout.length; i++) {
            const id = layout[i].i;
            if (id === 'add') continue;
            const idx = parseInt(id, 10);
            if (!isNaN(idx) && temp[idx])
              temp[idx] = { ...temp[idx], x: layout[i].x, y: layout[i].y };
          }

          onListChange(temp);
        }}
        draggableHandle=".handle"
        className="align-items-start w-100 h-100"
        style={previewAreaStyle}
      >
        {list &&
          list.map((item, index) => {
            if (item?.displayed === false || item?.active === false) return null;

            const widgetDef = definedWidgetData?.[item.id] || {};
            const variantKey = `${item.w}x${item.h}`;
            const previewSrc =
              widgetDef.previewImages?.[variantKey] || widgetDef.previewImages?.['1x1'] || null;

            const previewComponent = createPreviewComponent(widgetDef, previewSrc);

            return (
              <div
                key={String(index)}
                style={{ ['--preview-row-height']: `${previewRowHeight}px` }}
              >
                <Widget
                  key={String(index)}
                  item={{ ...widgetDef, ...item, component: previewComponent }}
                  index={String(index)}
                  expandable
                  list={list}
                  onListChange={onListChange}
                  uninteractable={uninteractable}
                  dashboardPreview={true}
                  scale={1}
                  removeWidget={(idx) => {
                    if (typeof onRemoveWidget === 'function') onRemoveWidget(Number(idx));
                  }}
                  allowDeleteInPreview={!!(onRemoveWidget && edit)}
                />
              </div>
            );
          })}

        {edit && (
          <div
            key="add"
            className="d-flex align-items-center justify-content-center rounded-2 overflow-hidden bg-base"
          >
            <AddWidgetComponent openWidgetSelector={() => openWidgetSelector(true)} />
          </div>
        )}
      </ResponsiveGridLayout>
    </div>
  );
}
